import { Tab } from "@headlessui/react";
// import CommentListing from "components/CommentListing/CommentListing";
import ExperiencesCard from "components/ExperiencesCard/ExperiencesCard";
import StartRating from "components/StartRating/StartRating";
import StayCard from "components/StayCard/StayCard";
import HolidayPackageCard from "components/HolidayPackageCard/HolidayPackageCard";
import { FC, useEffect, useState } from "react";
import Avatar from "shared/Avatar/Avatar";
// import ButtonSecondary from "shared/Button/ButtonSecondary";
import SocialsList from "shared/SocialsList/SocialsList";
import { Helmet } from "react-helmet";
import moment from "moment";
import { 
  useGetHostQuery,
  useGetHostPropertiesQuery, 
  useGetHostExperiencesQuery, 
  useGetHostHolidayPackagesQuery 
} from "application/features/host/hostApiSlice";
import Skeleton from 'react-loading-skeleton';
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

export interface HostPageProps {
  className?: string;
}

const HostPage: FC<HostPageProps> = ({ className = "" }) => {

  const { t, ready } = useTranslation("translation");

  const { host_uuid } = useParams<{ host_uuid: string }>()
  const { data: host, isLoading, isError } = useGetHostQuery(host_uuid);
  const { data: properties, isLoading: isLoadingProperties } = useGetHostPropertiesQuery(host_uuid);
  const { data: experiences, isLoading: isLoadingExperiences } = useGetHostExperiencesQuery(host_uuid);
  const { data: holidayPackages, isLoading: isLoadingHolidayPackages } = useGetHostHolidayPackagesQuery(host_uuid);
  const navigate = useNavigate();
  useEffect(() => {
    if (isError) {
      navigate("/404");
    }
  }, [isError, navigate]);

  let [categories, setCategories] = useState(["Pacchetti", "Strutture", "Esperienze"]);
  let [selectedIndex, setSelectedIndex] = useState(0); // 0: Holiday Packages, 1: Properties, 2: Experiences

  useEffect(() => {
    const newCategories = ["Pacchetti", "Strutture", "Esperienze"];
    if (isLoadingHolidayPackages || !holidayPackages || holidayPackages.length === 0) {
      newCategories.splice(newCategories.indexOf("Pacchetti"), 1);
    }
    if (isLoadingProperties || !properties || properties.length === 0) {
      newCategories.splice(newCategories.indexOf("Strutture"), 1);
    }
    if (isLoadingExperiences || !experiences || experiences.length === 0) {
      newCategories.splice(newCategories.indexOf("Esperienze"), 1);
    }
    setCategories(newCategories);
  }, [isLoadingHolidayPackages, holidayPackages, isLoadingProperties, properties, isLoadingExperiences, experiences]);

  const renderSidebar = () => {
    return (
      <div className=" w-full flex flex-col items-center text-center sm:rounded-2xl sm:border border-neutral-200 dark:border-neutral-700 space-y-6 sm:space-y-7 px-0 sm:p-6 xl:p-8">
        <Avatar
          hasChecked
          hasCheckedClass="w-6 h-6 -top-0.5 right-2"
          sizeClass="w-28 h-28"
          imgUrl={host.avatar ? host.avatar : ""}
        />

        {/* ---- */}
        <div className="space-y-3 text-center flex flex-col items-center">
          <h2 className="text-3xl font-semibold">{host.name}</h2>
          <StartRating point={host.rating} reviewCount={host.reviews_count} className="!text-base" />
        </div>

        {/* ---- */}
        <p className="text-neutral-500 dark:text-neutral-400">
          {host.description}
        </p>

        {/* ---- */}
        {host.socials &&
          <SocialsList
            className="!space-x-3"
            itemClass="flex items-center justify-center w-9 h-9 rounded-full bg-neutral-100 dark:bg-neutral-800 text-xl"
          />
        }

        {/* ---- */}
        <div className="border-b border-neutral-200 dark:border-neutral-700 w-14"></div>

        {/* ---- */}
        <div className="space-y-4">
          <div className="flex items-center space-x-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-neutral-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
              />
            </svg>
            <span className="text-neutral-6000 dark:text-neutral-300 capitalize">
              {host.city} - {host.state}
            </span>
          </div>
          <div className="flex items-center space-x-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-neutral-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            {
              host.created_at &&
              <span className="text-neutral-6000 dark:text-neutral-300">
                {t("translation:hostProfilePage.sidebar.memberSince")} {moment(host.created_at).format("DD MMM YYYY")}
              </span>
            }
          </div>
        </div>
      </div>
    );
  };

  const renderSection2 = () => {

    if (host.reviews_count === 0) {
      return null;
    }

    return (
      <div className="listingSection__wrap">
        {/* HEADING */}
        <h2 className="text-2xl font-semibold">{t("translation:hostProfilePage.reviews.title")}</h2>
        <span className="block mt-2 text-neutral-500 dark:text-neutral-400">
          {t("translation:hostProfilePage.reviews.description")}
        </span>
        <div className="w-14 border-b border-neutral-200 dark:border-neutral-700"></div>

        {/* comment */}
        <div className="divide-y divide-neutral-100 dark:divide-neutral-800">
          {/* <CommentListing hasListingTitle className="pb-8" />
          <CommentListing hasListingTitle className="py-8" />
          <CommentListing hasListingTitle className="py-8" />
          <CommentListing hasListingTitle className="py-8" /> */}
          {
            /*
           <div className="pt-8">
            <ButtonSecondary>View more 20 reviews</ButtonSecondary>
          </div>
          */}
        </div>
      </div>
    );
  };

  const renderListings = () => {
    const renderListingsGrid = () => {
      // Holiday Packages (Pacchetti) - index 0
      if (selectedIndex === 0) {
        if (isLoadingHolidayPackages) {
          return (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => <Skeleton key={i} className="h-80" />)}
            </div>
          );
        }
        if (!holidayPackages || holidayPackages.length === 0) {
          return (
            <div className="text-center py-12">
              <p className="text-neutral-500 dark:text-neutral-400">
                Nessun pacchetto disponibile
              </p>
            </div>
          );
        }
        return (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {holidayPackages.map((pkg: any) => (
              <HolidayPackageCard key={pkg.package_uuid} data={pkg} />
            ))}
          </div>
        );
      }

      // Properties/Stays (Strutture) - index 1
      if (selectedIndex === 1) {
        if (isLoadingProperties) {
          return (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => <Skeleton key={i} className="h-80" />)}
            </div>
          );
        }
        if (!properties || properties.length === 0) {
          return (
            <div className="text-center py-12">
              <p className="text-neutral-500 dark:text-neutral-400">
                Nessuna struttura disponibile
              </p>
            </div>
          );
        }
        return (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {properties.map((property: any) => (
              <StayCard key={property.property_uuid} data={property} />
            ))}
          </div>
        );
      }

      // Experiences (Esperienze) - index 2
      if (selectedIndex === 2) {
        if (isLoadingExperiences) {
          return (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => <Skeleton key={i} className="h-80" />)}
            </div>
          );
        }
        if (!experiences || experiences.length === 0) {
          return (
            <div className="text-center py-12">
              <p className="text-neutral-500 dark:text-neutral-400">
                Nessuna esperienza disponibile
              </p>
            </div>
          );
        }
        return (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {experiences.map((experience: any) => (
              <ExperiencesCard key={experience.experience_uuid} data={experience} />
            ))}
          </div>
        );
      }
    };

    return (
      <div className="listingSection__wrap">
        {/* TABS */}
        <Tab.Group selectedIndex={selectedIndex} onChange={setSelectedIndex}>
          <Tab.List className="flex space-x-2 border-b border-neutral-200 dark:border-neutral-700">
            {categories.map((category, idx) => (
              <Tab
                key={idx}
                className={({ selected }) =>
                  `py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                    selected
                      ? "border-primary-600 text-primary-600"
                      : "border-transparent text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-300"
                  }`
                }
              >
                {category}
              </Tab>
            ))}
          </Tab.List>
          <Tab.Panels className="mt-8">
            <Tab.Panel>
              {renderListingsGrid()}
            </Tab.Panel>
            <Tab.Panel>
              {renderListingsGrid()}
            </Tab.Panel>
            <Tab.Panel>
              {renderListingsGrid()}
            </Tab.Panel>
          </Tab.Panels>
        </Tab.Group>
      </div>
    );
  };

  if (!ready) return null;

  return (
    <div className={`nc-AuthorPage ${className}`} data-nc-id="AuthorPage">
      <Helmet>
        <title>{t("translation:hostProfilePage.title")} || TRIBYOU</title>
      </Helmet>
      <main className="container mt-12 mb-24 lg:mb-32 flex flex-col lg:flex-row">
        <div className="block flex-grow mb-24 lg:mb-0">
          <div className="lg:sticky lg:top-24">{!isLoading ? renderSidebar() : <Skeleton className="h-[20rem]" />}</div>
        </div>
        <div className="w-full lg:w-3/5 xl:w-2/3 space-y-8 lg:space-y-10 lg:pl-10 flex-shrink-0">
          {host && !isLoading ? renderListings() : <Skeleton className="h-[40rem]" />}
          {host && !isLoading ? renderSection2() : <Skeleton className="h-[20rem]" />}
        </div>
      </main>
    </div>
  );
};

export default HostPage;
